import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { MockVideo } from '@/hooks/video-generator/useVideoGeneration';
import { toast } from '@/hooks/errors/use-toast';
import { X } from 'lucide-react';
import { VideoMetadataSidebar } from './components/VideoMetadataSidebar';
import { VideoDisplay } from './components/VideoDisplay';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { useSmartVideoNavigation } from '@/hooks/video-lightbox/useSmartVideoNavigation';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface VideoDetailConfig {
  preloading?: {
    enabled: boolean;
    strategy: 'aggressive' | 'conservative' | 'smart';
    preloadDistance: number;
  };
  navigation?: {
    wheel: boolean;
    keyboard: boolean;
    clickZones: boolean;
  };
  mobile?: {
    fullscreen: boolean;
    sidebarPosition: 'bottom' | 'overlay' | 'hidden';
  };
}

interface VideoDetailProps<T = MockVideo> {
  video: T | null;
  isOpen: boolean;
  onClose: () => void;
  allVideos?: T[];
  onNavigate?: (video: T) => void;
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  config?: VideoDetailConfig;
  
  // Property accessors for generic types
  getVideoUrl?: (item: T) => string;
  getVideoId?: (item: T) => string;
  getVideoTitle?: (item: T) => string | undefined;
  getVideoPrompt?: (item: T) => string | undefined;
  getDuration?: (item: T) => number | undefined;
  getHasAudio?: (item: T) => boolean | undefined;
  getLikeCount?: (item: T) => number | undefined;
  getIsLiked?: (item: T) => boolean | undefined;
  
  // Custom sidebar rendering
  renderSidebar?: (item: T, context: SidebarContext<T>) => React.ReactNode;
  
  // Custom delete/download handlers
  onDelete?: (item: T) => Promise<void>;
  onDownload?: (item: T) => Promise<void>;
  onLike?: (item: T) => Promise<void>;
  checkOwnership?: (item: T) => Promise<boolean> | boolean;
  
  // Display options
  showLikeButton?: boolean;
  showDescription?: boolean;
  dropdownLabel?: string;
}

interface SidebarContext<T> {
  item: T;
  isOwner: boolean;
  isDeleteLoading: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onLoadMore?: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onLike: () => void;
}

const DEFAULT_CONFIG: VideoDetailConfig = {
  preloading: {
    enabled: true,
    strategy: 'smart',
    preloadDistance: 2
  },
  navigation: {
    wheel: true,
    keyboard: true,
    clickZones: true
  },
  mobile: {
    fullscreen: true,
    sidebarPosition: 'bottom'
  }
};

const VideoDetailComponent = <T = MockVideo>(props: VideoDetailProps<T>) => {
  const {
    video,
    isOpen,
    onClose,
    allVideos = [],
    onNavigate,
    loadMore,
    hasNextPage = false,
    isFetchingNextPage = false,
    config = DEFAULT_CONFIG,
    
    // Property accessors with defaults for MockVideo
    getVideoUrl = (item: T) => (item as any).videoUrl,
    getVideoId = (item: T) => (item as any).id,
    getVideoTitle = (item: T) => (item as any).title,
    getVideoPrompt = (item: T) => (item as any).originalPrompt,
    getDuration = (item: T) => (item as any).duration,
    getHasAudio = (item: T) => (item as any).hasAudio,
    getLikeCount = (item: T) => (item as any).likeCount,
    getIsLiked = (item: T) => (item as any).isLiked,
    
    // Custom handlers
    renderSidebar,
    onDelete: customOnDelete,
    onDownload: customOnDownload,
    onLike: customOnLike,
    checkOwnership: customCheckOwnership,
    
    // Display options
    showLikeButton = true,
    showDescription = true,
    dropdownLabel
  } = props;

  const { isMobile } = useIsMobile();

  // Merge config with defaults and disable wheel navigation on mobile
  const mergedConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    preloading: { ...DEFAULT_CONFIG.preloading, ...config?.preloading },
    navigation: { 
      ...DEFAULT_CONFIG.navigation, 
      ...config?.navigation,
      // Disable wheel navigation on mobile to allow normal scrolling
      wheel: isMobile ? false : (config?.navigation?.wheel ?? DEFAULT_CONFIG.navigation.wheel)
    },
    mobile: { ...DEFAULT_CONFIG.mobile, ...config?.mobile }
  };

  // Always call useSmartVideoNavigation to maintain hook order
  const {
    hasPrevious,
    hasNext,
    canNavigateNext,
    handlePrevious,
    handleNext,
    handleWheel
  } = useSmartVideoNavigation({
    currentItem: video,
    allItems: allVideos,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    onNavigate,
    getItemId: getVideoId,
    config: mergedConfig
  });

  // Delete functionality with improved error handling
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Ownership check with error handling
  const [isOwner, setIsOwner] = useState(false);
  
  useEffect(() => {
    const checkOwnership = async () => {
      if (!video) return;
      
      try {
        if (customCheckOwnership) {
          const isOwner = await customCheckOwnership(video);
          setIsOwner(isOwner);
          return;
        }
        
        // For mock videos, assume ownership for demo
        setIsOwner(true);
      } catch (error) {
        console.error('Error checking ownership:', error);
        setIsOwner(false);
      }
    };
    
    checkOwnership();
  }, [video, customCheckOwnership]);

  // Download with comprehensive error handling
  const handleDownload = async () => {
    if (!video) return;
    
    try {
      if (customOnDownload) {
        await customOnDownload(video);
        return;
      }
      
      // Default download logic for videos
      const videoUrl = getVideoUrl(video);
      const videoId = getVideoId(video);
      
      // For mock videos, show placeholder toast
      toast({
        title: "Download started",
        description: "Video download functionality will be implemented with real videos.",
        variant: "default"
      });
    } catch (error) {
      console.error('Error downloading video:', error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Failed to download video. Please try again.",
        variant: "error"
      });
    }
  };

  // Like functionality
  const handleLike = async () => {
    if (!video) return;
    
    try {
      if (customOnLike) {
        await customOnLike(video);
        return;
      }
      
      // Default like logic
      toast({
        title: "Like toggled",
        description: "Like functionality will be implemented with real videos.",
        variant: "default"
      });
    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        title: "Like failed",
        description: error instanceof Error ? error.message : "Failed to update like status. Please try again.",
        variant: "error"
      });
    }
  };
  
  // Delete with improved error handling
  const handleDelete = () => {
    if (!video) return;
    
    const videoId = getVideoId(video);
    if (!isOwner || !videoId) return;
    setShowDeleteConfirmation(true);
  };
  
  const confirmDelete = async () => {
    if (!video) return;
    
    const videoId = getVideoId(video);
    if (!isOwner || !videoId) return;
    
    try {
      setIsDeleteLoading(true);
      
      if (customOnDelete) {
        await customOnDelete(video);
      } else {
        // Default delete logic for mock videos
        toast({
          title: "Video deleted",
          description: "Delete functionality will be implemented with real videos.",
          variant: "default"
        });
      }
      
      setShowDeleteConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "There was a problem deleting the video. Please try again.",
        variant: "error"
      });
    } finally {
      setIsDeleteLoading(false);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Now handle the conditional rendering AFTER all hooks have been called
  if (!video) {
    return (
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="hidden">
          <DialogTitle className="sr-only">No Video</DialogTitle>
          <DialogDescription className="sr-only">No video selected</DialogDescription>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {/* Enhanced confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirmation}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title="Delete Video"
        description="Are you sure you want to delete this video? This action cannot be undone."
        confirmText={isDeleteLoading ? "Deleting..." : "Delete Video"}
        cancelText="Keep Video"
        confirmVariant="destructive"
        cancelVariant="default"
      />
      
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent 
          showCloseButton={false}
          className={`
            ${mergedConfig.mobile?.fullscreen 
              ? 'h-screen w-screen md:max-w-[95vw] md:max-h-[95vh] md:w-auto md:h-auto' 
              : 'max-w-[95vw] max-h-[95vh] w-auto h-auto'
            } 
            p-0 cursor-default
            ${isMobile ? 'overflow-auto !top-0 !left-0 !transform-none !translate-x-0 !translate-y-0' : ''}
          `}
          onPointerDownOutside={(e) => {
            // Prevent closing when clicking on navigation buttons
            const target = e.target as HTMLElement;
            if (target.closest('[data-nav-button]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogTitle className="sr-only">Video Details</DialogTitle>
          <DialogDescription className="sr-only">
            {getVideoPrompt(video) || 'Generated video details and metadata'}
          </DialogDescription>
          
          <div className={`
            relative flex 
            ${mergedConfig.mobile?.sidebarPosition === 'bottom' ? 'flex-col md:flex-row' : 'flex-col md:flex-row'} 
            w-full h-full
          `}>
            
            {/* Close button */}
            <div className="fixed top-4 right-4 z-50">
              <DialogClose className="rounded-full h-8 w-8 flex items-center justify-center bg-background/90 hover:bg-background border border-border shadow-sm transition-all cursor-pointer">
                <X className="h-4 w-4" />
              </DialogClose>
            </div>
            
            {/* Main content area with sophisticated navigation */}
            <VideoDisplay 
              videoUrl={getVideoUrl(video)} 
              title={getVideoTitle(video)}
              description={getVideoPrompt(video)} 
              videoId={getVideoId(video)}
              duration={getDuration(video)}
              hasAudio={getHasAudio(video)}
              initialLikeCount={getLikeCount(video) || 0}
              isLiked={getIsLiked(video) || false}
              hasPrevious={hasPrevious}
              hasNext={canNavigateNext}
              onPrevious={mergedConfig.navigation?.clickZones ? handlePrevious : undefined}
              onNext={mergedConfig.navigation?.clickZones ? handleNext : undefined}
              onWheel={mergedConfig.navigation?.wheel ? handleWheel : undefined}
              onLike={handleLike}
              showLikeButton={showLikeButton}
              showDescription={showDescription}
              dropdownLabel={dropdownLabel}
              key={getVideoId(video)} // Force re-render when video changes
            />
            
            {/* Render custom sidebar or default metadata sidebar */}
            <div key="sidebar-container">
              {renderSidebar ? (
                <div key="custom-sidebar">
                  {renderSidebar(video, {
                    item: video,
                    isOwner,
                    isDeleteLoading,
                    hasPrevious,
                    hasNext,
                    isLoadingMore: isFetchingNextPage,
                    hasNextPage,
                    onPrevious: handlePrevious,
                    onNext: handleNext,
                    onLoadMore: loadMore,
                    onDownload: handleDownload,
                    onDelete: handleDelete,
                    onLike: handleLike
                  })}
                </div>
              ) : (
                <div key="default-sidebar">
                  <VideoMetadataSidebar 
                    video={video as MockVideo}
                    isOwner={isOwner}
                    isDeleteLoading={isDeleteLoading}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onLike={handleLike}
                    hasPrevious={hasPrevious}
                    hasNext={hasNext}
                    isLoadingMore={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onLoadMore={loadMore}
                  />
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Create a memoized version with proper typing
const VideoDetail = React.memo(VideoDetailComponent) as typeof VideoDetailComponent;

export default VideoDetail; 